import axios, {
  AxiosRequestConfig,
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import { message } from 'antd'
import { isNumber } from 'lodash-es'
import qs from 'qs'
import { getStorage } from '@/utils/tools'
import { loginAgainConfirm } from '@/utils/help'

export interface ResponseData<T> {
  data: T
  result?: T
  code?: number
  msg?: string
  success?: boolean
  message?: string
}

export type PlatformType = 'api' | 'centerApi' | 'custom' | 'none' | 'rcj'

const ConfigApi = {
  development: 'http://192.168.13.141:3080',
  // development: 'http://localhost:3080',
  qa: 'http://192.168.13.141:3080',
  pre: window.BASE_URL || 'http://zjy-pre.lubansoft.com/gateway',
  production: window.BASE_URL || 'https://zjy.lubansoft.com/gateway',
} as Record<string, string>


export function getUrl(path: string, platform = 'api'): string {
  let baseUrl;
  if (platform === 'api') {
    baseUrl = ConfigApi[process.env.NODE_STAGE || 'development'];
  }
  if (platform === 'none') {
    return path
  }
  return `${baseUrl}${path}`;
}

// 创建新的axios实例。不然会和造价云的axios全局配置冲突
const axiosInstance = axios.create({
  headers: {
    'Content-Type': 'application/json;charset=utf-8'
  },
  withCredentials: false
})

// 添加请求拦截器
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (!config.url?.includes('/login')) {
      const token = getStorage('token')
      if (token) {
        config.headers['access-token'] = token
      }
    }
    return config
  },
  (error: AxiosError) => Promise.reject(error),
)

// 添加响应拦截器
axiosInstance.interceptors.response.use(
  (response: AxiosResponse<ResponseData<any>>) => {
    if (!response?.data?.code) {
      return response
    }

    const { code, msg } = response.data

    if (code === 401 || code === 1002 || code === 1007 || code === 1021 || code === 1013) {
      loginAgainConfirm()
      return Promise.reject(response)
    }

    const errCodes = [
      500, 1007, 40000, -119, 0, 50000, 1006, 1035, 1037, 1021, 400, -102, 1003, 1014, 1015, 1016, -1
    ]
    if (isNumber(code) && errCodes.includes(code)) {
      message.error(response.data.msg || response.data.message)
      return Promise.reject(response)
    }
    return response
  },
  (error: AxiosError<ResponseData<any>>) => {
    console.log('error :>> ', error)
    try {
      const { response } = error
      const msgInfo = response?.data?.msg || response?.data?.message || '服务错误'
      
      if (response?.status === 401) {
        loginAgainConfirm()
        return Promise.reject(response)
      }
      if (!response) {
        message.error({ content: '服务错误', key: 'response-error' })
        return Promise.reject('服务错误')
      }
      if (response.status === 403) {
        message.error('没有当前功能权限，请联系管理员')
        return Promise.reject(response)
      }
      if (response.status === 400) {
        message.error(msgInfo)
        return Promise.reject(response)
      }
      if (response.status === 504) {
        message.error('服务器超时，请稍后再试')
        return Promise.reject(response)
      }
      return Promise.reject(response)
    } catch (e) {
      console.log('error', e)
    }
    return error
  },
)

interface ParamT {
  [name: string]: any
}

export async function request<T>(
  path: string,
  params?: ParamT | FormData,
  options?: AxiosRequestConfig,
  platform: PlatformType = 'api',
): Promise<T> {
  const url = getUrl(path, platform)
  const opts = {
    ...options,
    url,
    data: params,
  }
  if (options.method.toLowerCase() === 'get' || options.method.toLowerCase() === 'delete') {
    const query = params ? `?${qs.stringify(params)}` : ''
    opts.url = `${url}${query}`
  }
  const axiosResult = await axiosInstance.request(opts)
  return axiosResult?.data?.data || axiosResult?.data?.result || axiosResult?.data
}

export function get<T>(
  path: string,
  params?: ParamT,
  config?: AxiosRequestConfig,
  platform: PlatformType = 'api'
) {
  return request<T>(path, params, {
    method: 'GET',
    ...config,
  }, platform);
}

export function post<T>(
  path: string,
  params?: ParamT,
  config?: AxiosRequestConfig,
  platform: PlatformType = 'api'
) {
  return request<T>(path, params, {
    method: 'POST',
    ...config,
  }, platform)
}

export function patch<T>(
  path: string,
  params?: ParamT,
  config?: AxiosRequestConfig,
  platform: PlatformType = 'api'
) {
  return request<T>(path, params, {
    method: 'PATCH',
    ...config,
  }, platform)
}

export function put<T>(
  path: string,
  params?: ParamT,
  config?: AxiosRequestConfig,
  platform: PlatformType = 'api'
) {
  return request<T>(path, params, {
    method: 'PUT',
    ...config,
  }, platform)
}

export function remove<T>(
  path: string,
  params?: ParamT,
  config?: AxiosRequestConfig,
  platform: PlatformType = 'api'
) {
  return request<T>(path, params, {
    method: 'DELETE',
    ...config,
  }, platform)
}

export default request